import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { fetchAllPaginated } from "@/lib/supabase-paginate";
import { useBillingRates } from "./use-billing-rates";
import { calcBfYearContributions, calcBfYearTotals, type BfBucketProject } from "@/lib/bf-buckets";
import {
  bonusDeviationProjectIds,
  hoursByProject,
  rateMetricOutlook,
  type ProjectHours,
} from "@/lib/bonus-goals";
import { useReportingReconciliation } from "./use-reporting-reconciliation";

export interface BonusGoal {
  id: number;
  title: string;
  description: string;
  target: number;
  current: number;
  unit: string;
  progress: number; // 0-100
  achieved: boolean;
  amountNok: number;
  /**
   * `cumulative`: tallet bygger seg opp gjennom året mot et mål (vis
   * progress-bar med "Hittil i år"-etikett).
   * `state`: tallet er en tilstand som svinger gjennom året (skjul
   * progress-bar, vis mål-linje og delta).
   */
  kind: "cumulative" | "state";
  /**
   * `min`: bonus utløses når `current >= target` (f.eks. BF, faktureringsgrad).
   * `max`: bonus utløses når `current <= target` (f.eks. budsjettavvik).
   */
  targetDirection: "min" | "max";
  /** Når true: vis fortegn (+/−) på `current`. Default false. */
  signed?: boolean;
  /** Kun GEEK/BF-målet: faktisk oppnådd grunnlag i MNOK. */
  oppnadd?: number;
  /** Kun GEEK/BF-målet: forventet tillegg resten av året i MNOK. */
  prognose?: number | null;
  /** Total årsprognose (oppnådd + forventet tillegg) i MNOK. */
  forecastTotal?: number | null;
  /** Aktiverer delt badge-semantikk fra widget-status. */
  metricStatus?: boolean;
  /**
   * Kun faktureringsgrad-målet: grad justert for inneværende måned
   * (pro-ratet kapasitet). Display-only — påvirker ikke `achieved`/bonus.
   * null når inneværende måned ikke kan beregnes (f.eks. tidligere år).
   */
  adjustedCurrent?: number | null;
  /** Fremoverskuende steady-state-forklaring for rate-mål. */
  outlook?: string;
}

/** Prosjektene bak GEEK- og avviksmålet, til drilldown (VIB-2990). */
export interface BonusGoalDetails {
  projectNames: Map<string, { name: string; code: string | null }>;
  geek: Array<{ projectId: string; kind: "reell" | "prognose"; amount: number; estimatedDeadline: string | null }>;
  deviation: ProjectHours[];
}

interface BonusGoalsData {
  year: number;
  details: BonusGoalDetails;
  goals: BonusGoal[];
  totalEarned: number;
  totalPotential: number;
  reconciliationStatus: string | null;
}

const BONUS_AMOUNT_PER_GOAL = 12_000;
const BF_TARGET_MNOK = 15;
const BILLING_RATE_TARGET_PCT = 68;
const DEVIATION_TARGET_PCT = 8; // maks +8% over solgte timer
const DEVIATION_ZERO_PROGRESS_PCT = 18; // ved +18% over: 0% progress (10 pp over målet)

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/**
 * Bonusmål YTD for valgt år. Tre selskapsmål som gjelder alle ansatte og
 * trigger en bonus på 12 000 kr hver ved måloppnåelse.
 *
 * - BF: sum mål-GEEK fra ferdigstilte prosjekter med `completed_at` i året
 *   (2026 trekker ut arbeid fra før, jf. geekForMaal). Mål: 15 MNOK.
 * - Faktureringsgrad: `useBillingRates(year).teamYtdRate` (timer-basert per
 *   CLAUDE.md kjerneberegning). Mål: 68%.
 * - Budsjettavvik: aggregert (brukt − solgt) / solgt × 100 over alle
 *   ferdigstilte prosjekter YTD. Positivt = over budsjett. Mål: maks +8%.
 */
export function useBonusGoals(year: number = new Date().getFullYear()) {
  const billingRates = useBillingRates(year);
  const reporting = useReportingReconciliation(year);
  const reconciledThroughIso = reporting.enabled
    ? year === reporting.period.currentYear
      ? reporting.period.reconciledThroughIso
      : year < reporting.period.currentYear
        ? `${year}-12-31`
        : undefined
    : undefined;

  const goalsQuery = useQuery({
    queryKey: ["bonus-goals", { year, reconciledThroughIso: reconciledThroughIso ?? "legacy" }],
    queryFn: async () => {
      // Reell + prognose fra den DELTE fasiten (samme som BF-over-tid m.fl.).
      const projectsRes = await supabase
        .from("projects")
        .select("id, name, project_code, project_status, bf_actual, bf_estimated, completed_at, estimated_deadline, is_internal")
        .is("deleted_at", null);
      if (projectsRes.error) throw new Error(projectsRes.error.message);

      const allProjects = (projectsRes.data ?? []) as Array<{ id: string; name: string } & BfBucketProject>;
      // VIB-1689 (Erik-avklart 2026-08-07): GEEK-målet bruker ALLTID fasiten,
      // uavhengig av avstemmingsflagget. Reell = Σ mål-GEEK (avsluttede),
      // prognose = Σ bf_estimated (pågående/kommende med deadline i året) —
      // dvs. opptjent + ufakturert andel samlet. Avstemmingen styrer fortsatt
      // faktureringsgrad- og budsjettavvik-målene under.
      const totals = calcBfYearTotals(allProjects, year);

      // Ferdigstilte i år → grunnlag for budsjettavviks-målet.
      const projectIds = bonusDeviationProjectIds(allProjects, year, reconciledThroughIso);

      let deviation: ProjectHours[] = [];

      if (projectIds.length > 0) {
        // "Solgte timer" = budget_lines.quantity_hours på interne linjer
        // (matcher prosjektkortet, og det er det som faktisk ble solgt i
        // tilbudet — assignments er en operasjonell fordeling som ofte
        // mangler på eldre/importerte prosjekter selv om timene er solgt).
        // time_entries paginreres: completed-prosjektene kan ha >1000 rader
        // (Supabase JS kutter stille ved 1000 ellers → spent under-telles og
        // avviket blir falskt negativt). Antallet vokser gjennom året.
        const [budgetLineRows, timeEntryRows] = await Promise.all([
          // budget_lines pagineres også — team-bred read som vokser mot
          // 1000-rad-taket. VIB-1249.
          fetchAllPaginated<{ project_id: string; quantity_hours: number | null }>(
            "budget_lines",
            "project_id, quantity_hours",
            (q) => q.in("project_id", projectIds).eq("cost_type", "internal"),
          ),
          fetchAllPaginated<{ project_id: string; duration_seconds: number | null }>(
            "time_entries",
            "project_id, duration_seconds",
            (q) => q.in("project_id", projectIds).is("deleted_at", null),
          ),
        ]);

        deviation = hoursByProject(budgetLineRows, timeEntryRows);
      }

      const totalSold = deviation.reduce((sum, p) => sum + p.soldHours, 0);
      const totalSpent = deviation.reduce((sum, p) => sum + p.spentHours, 0);
      const details: BonusGoalDetails = {
        projectNames: new Map(allProjects.map((p) => [p.id, { name: p.name, code: p.project_code ?? null }])),
        geek: calcBfYearContributions(allProjects, year).contributions.map((c) => ({
          projectId: c.project.id,
          kind: c.kind,
          amount: c.amount,
          estimatedDeadline: c.project.estimated_deadline,
        })),
        deviation,
      };

      return { reellYtd: totals.reellYtd, prognose: totals.prognose, totalSold, totalSpent, details };
    },
    staleTime: 5 * 60 * 1000,
  });

  const isLoading = billingRates.isLoading || goalsQuery.isLoading;
  const error = billingRates.error ?? goalsQuery.error;

  let data: BonusGoalsData | undefined;
  if (!isLoading && !error && goalsQuery.data && billingRates.data) {
    const { reellYtd, prognose, totalSold, totalSpent, details } = goalsQuery.data;
    const teamRate = billingRates.data.teamYtdRate;

    // Live-graden er display-only. Bonus styres fortsatt av avstemt teamYtdRate.
    const adjustedRate = billingRates.data.teamLiveYtdRate;

    const bfMnok = reellYtd / 1_000_000;
    const bfForecastMnok = prognose / 1_000_000;
    const bfForecastTotalMnok = (reellYtd + prognose) / 1_000_000;
    const bfAchieved = bfMnok >= BF_TARGET_MNOK;

    const billingAchieved = teamRate >= BILLING_RATE_TARGET_PCT;

    const deviationPct =
      totalSold > 0 ? ((totalSpent - totalSold) / totalSold) * 100 : 0;
    const deviationAchieved = totalSold > 0 && deviationPct <= DEVIATION_TARGET_PCT;
    // Progress: 100% når avvik ≤ +5%, 0% når avvik ≥ +15%, lineært imellom.
    const deviationProgress = clamp(
      ((DEVIATION_ZERO_PROGRESS_PCT - deviationPct) /
        (DEVIATION_ZERO_PROGRESS_PCT - DEVIATION_TARGET_PCT)) *
        100,
      0,
      100,
    );

    const goals: BonusGoal[] = [
      {
        id: 1,
        title: "GEEK",
        description: "Team-GEEK: oppnådd fra ferdigstilte + prognose fra pågående",
        target: BF_TARGET_MNOK,
        current: Math.round(bfMnok * 100) / 100,
        unit: "MNOK",
        progress: clamp((bfMnok / BF_TARGET_MNOK) * 100, 0, 100),
        achieved: bfAchieved,
        amountNok: BONUS_AMOUNT_PER_GOAL,
        kind: "cumulative",
        targetDirection: "min",
        oppnadd: Math.round(bfMnok * 100) / 100,
        prognose: Math.round(bfForecastMnok * 100) / 100,
        forecastTotal: Math.round(bfForecastTotalMnok * 100) / 100,
        metricStatus: reporting.enabled,
      },
      {
        id: 2,
        title: "Faktureringsgrad",
        description: "Teamets fakturerbare andel av timene hittil i år",
        target: BILLING_RATE_TARGET_PCT,
        current: Math.round(teamRate * 10) / 10,
        unit: "%",
        progress: clamp((teamRate / BILLING_RATE_TARGET_PCT) * 100, 0, 100),
        achieved: billingAchieved,
        amountNok: BONUS_AMOUNT_PER_GOAL,
        kind: "state",
        targetDirection: "min",
        metricStatus: reporting.enabled,
        adjustedCurrent:
          adjustedRate != null ? Math.round(adjustedRate * 10) / 10 : null,
        outlook: reporting.enabled
          ? rateMetricOutlook({
              achieved: billingAchieved,
              progress: clamp(
                (teamRate / BILLING_RATE_TARGET_PCT) * 100,
                0,
                100,
              ),
            })
          : undefined,
      },
      {
        id: 3,
        title: "Budsjetteringsavvik",
        description: "Brukte vs. solgte timer på ferdigstilte prosjekter hittil i år",
        target: DEVIATION_TARGET_PCT,
        current: totalSold > 0 ? Math.round(deviationPct * 10) / 10 : 0,
        unit: "%",
        progress: totalSold > 0 ? deviationProgress : 0,
        achieved: deviationAchieved,
        amountNok: BONUS_AMOUNT_PER_GOAL,
        signed: true,
        kind: "state",
        targetDirection: "max",
        metricStatus: reporting.enabled,
        outlook: reporting.enabled
          ? rateMetricOutlook({
              achieved: deviationAchieved,
              progress: totalSold > 0 ? deviationProgress : 0,
            })
          : undefined,
      },
    ];

    const totalEarned = goals
      .filter((g) => g.achieved)
      .reduce((sum, g) => sum + g.amountNok, 0);
    const totalPotential = goals.reduce((sum, g) => sum + g.amountNok, 0);

    data = {
      year,
      details,
      goals,
      totalEarned,
      totalPotential,
      reconciliationStatus: reporting.enabled ? reporting.statusText : null,
    };
  }

  return { data, isLoading, error };
}
